import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload } from 'lucide-react';

interface PromoCode {
  id: string;
  code: string;
  is_active: boolean;
  uses_remaining: number;
  tier_granted: string;
  created_at: string;
  used_count: number;
}

export const PromoCodeManager = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [usesRemaining, setUsesRemaining] = useState(1);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPromoCodes(data || []);
    } catch (error) {
      toast.error('Failed to fetch promo codes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const createPromoCode = async () => {
    if (!newCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    try {
      const { error } = await supabase
        .from('promo_codes')
        .insert({
          code: newCode.toUpperCase(),
          uses_remaining: usesRemaining,
          tier_granted: 'professional'
        });

      if (error) throw error;

      toast.success('Promo code created successfully');
      setNewCode('');
      setUsesRemaining(1);
      setDialogOpen(false);
      fetchPromoCodes();
    } catch (error: any) {
      if (error.code === '23505') {
        toast.error('Promo code already exists');
      } else {
        toast.error('Failed to create promo code');
      }
      console.error(error);
    }
  };

  const togglePromoCode = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('promo_codes')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Promo code ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to update promo code');
      console.error(error);
    }
  };

  const deletePromoCode = async (id: string) => {
    if (!confirm('Are you sure you want to delete this promo code?')) return;

    try {
      const { error } = await supabase
        .from('promo_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
      console.error(error);
    }
  };

  const handleCsvImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const codes = lines.map(line => line.split(',')[0].trim().toUpperCase()).filter(code => code);

      if (codes.length === 0) {
        toast.error('No valid codes found in CSV file');
        return;
      }

      // Import codes in batches to avoid overwhelming the database
      const batchSize = 50;
      let importedCount = 0;
      let skippedCount = 0;

      for (let i = 0; i < codes.length; i += batchSize) {
        const batch = codes.slice(i, i + batchSize);
        const codeData = batch.map(code => ({
          code,
          is_active: true,
          uses_remaining: 1,
          tier_granted: 'professional',
          used_count: 0
        }));

        const { data, error } = await supabase
          .from('promo_codes')
          .insert(codeData)
          .select();

        if (error) {
          // Handle duplicate codes gracefully
          if (error.code === '23505') {
            skippedCount += batch.length;
            continue;
          }
          throw error;
        }

        importedCount += data?.length || 0;
      }

      toast.success(`Import completed! ${importedCount} codes imported, ${skippedCount} skipped (duplicates)`);
      setImportDialogOpen(false);
      fetchPromoCodes();
      
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    } catch (error) {
      toast.error('Failed to import CSV file');
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Promo Code Management</CardTitle>
            <CardDescription>
              Create and manage lifetime offer promo codes
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Import Promo Codes from CSV</DialogTitle>
                  <DialogDescription>
                    Upload a CSV file with promo codes in the first column. Each code will be set with 1 use remaining and professional tier access.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="csv-file">CSV File</Label>
                    <Input
                      id="csv-file"
                      type="file"
                      accept=".csv"
                      onChange={handleCsvImport}
                      disabled={importing}
                    />
                    <p className="text-sm text-muted-foreground">
                      Expected format: One promo code per line in the first column
                    </p>
                  </div>
                  {importing && (
                    <div className="text-center py-4">
                      <p className="text-sm text-muted-foreground">Importing codes...</p>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Code
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Promo Code</DialogTitle>
                  <DialogDescription>
                    Create a new promo code for lifetime professional access
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="promo-code">Promo Code</Label>
                    <Input
                      id="promo-code"
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                      placeholder="Enter promo code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="uses">Number of Uses</Label>
                    <Input
                      id="uses"
                      type="number"
                      min="1"
                      value={usesRemaining}
                      onChange={(e) => setUsesRemaining(parseInt(e.target.value) || 1)}
                      placeholder="Number of uses"
                    />
                  </div>
                  <Button onClick={createPromoCode} className="w-full">
                    Create Promo Code
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uses Remaining</TableHead>
                  <TableHead>Used Count</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promoCodes.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell className="font-mono">{code.code}</TableCell>
                    <TableCell>
                      <Badge variant={code.is_active ? 'default' : 'secondary'}>
                        {code.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>{code.uses_remaining}</TableCell>
                    <TableCell>{code.used_count}</TableCell>
                    <TableCell>
                      {new Date(code.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => togglePromoCode(code.id, code.is_active)}
                        >
                          {code.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deletePromoCode(code.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {promoCodes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-4 text-muted-foreground">
                      No promo codes found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};